import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import { serializeGroupValue } from '../utils/group';
import '../../../desktopstyle.css';

const CheckSVG = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="5 13 10 18 19 7" />
  </svg>
);

const WrenchSVG = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.4 2.4-2-.6-.6-2 2.4-2.4Z" />
  </svg>
);

const getStageStatus = (timingPhase) => {
  if (timingPhase === 'FINISHED')    return 'done';
  if (timingPhase === 'IN_PROGRESS') return 'live';
  return 'upcoming';
};

const SVC_COLOR = '#a0a8c0';

const formatDuration = (minutes) => {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60).toString().padStart(2, '0');
  const m = (minutes % 60).toString().padStart(2, '0');
  return `${h}:${m} min`;
};

const PHASE_DOT = {
  NOT_STARTED: { color: '#6c757d', bg: 'transparent' },
  IN_PROGRESS:  { color: '#e0a800', bg: 'rgba(224,168,0,0.10)' },
  FINISHED:     { color: '#28a745', bg: 'rgba(40,167,69,0.10)' },
};


const MobileDropdown = ({ children, colorClass = '', label, onClick }) => (
  <button type="button" className={`mobile-dropdown ${colorClass}`} onClick={onClick}>
    <span className="mobile-dropdown__value">{children}</span>
    {label && <span className="mobile-dropdown__label">{label}</span>}
  </button>
);

const OptionSheet = ({ title, options, selectedValue, onSelect, onClose, renderLabel }) => (
  <div className="mobile-sheet" role="dialog" aria-modal="true">
    <div className="mobile-sheet__backdrop" onClick={onClose} />
    <div className="mobile-sheet__content">
      <div className="mobile-sheet__drag-handle" />
      <div className="mobile-sheet__title">{title}</div>
      <div className="mobile-sheet__list">
        {options.map((option, i) =>
          option.kind === 'info' ? (
            <div
              key={`info-${i}`}
              style={{
                padding: '8px 16px',
                fontSize: '0.8em',
                color: SVC_COLOR,
                background: 'transparent',
                borderTop: `1px dashed ${SVC_COLOR}`,
                borderBottom: `1px dashed ${SVC_COLOR}`,
                margin: '2px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                textAlign: 'center',
              }}
            >
              <FontAwesomeIcon icon={faScrewdriverWrench} style={{ flexShrink: 0, width: '1em', height: '1em' }} />
              <span>
                {option.duration || option.name}
                {option.detail && (
                  <span style={{ opacity: 0.7, marginLeft: 6 }}>{option.detail}</span>
                )}
              </span>
            </div>
          ) : (
            <button
              key={option.value}
              type="button"
              className={`mobile-sheet__option ${
                selectedValue === option.value ? 'is-active' : ''
              }`}
              onClick={() => {
                onSelect(option.value);
                onClose();
              }}
            >
              {renderLabel ? renderLabel(option) : option.label}
            </button>
          )
        )}
      </div>
    </div>
  </div>
);

const ResultsFilters = ({
  competitions,
  competitionIndex,
  classificationIndex,
  stageIndex,
  groupOptions,
  selectedGroupValue,
  onCompetitionChange,
  onClassificationChange,
  onGroupChange,
  onStageChange,
  isMobile,
  viewMode,
  onViewModeChange = () => {},
  stagePhases,
}) => {
  const [activeSheet, setActiveSheet] = React.useState(null);
  // Żywa faza z WS ma pierwszeństwo przed snapshotem REST z wczytania strony.
  const resolvePhase = (stage) =>
    stagePhases?.[String(stage.StageID)] ?? stage.TimingPhase ?? 'NOT_STARTED';
  const activeCompetition = competitions[competitionIndex] || competitions[0] || null;
  const classifications = activeCompetition?.Classifications || [];
  const stages = activeCompetition?.Stages || [];
  const services = activeCompetition?.Services || [];
  const activeClassification = classifications[classificationIndex] || classifications[0] || null;
  const activeStage = stages[stageIndex] || stages[0] || null;
  const logo = activeClassification?.LogoClassification;

  // Combined sorted list for desktop steps and mobile sheet
  const combinedStageRows = useMemo(() => {
    const rows = [
      ...stages.map((s, i) => ({ kind: 'stage', stage: s, index: i, sortKey: (s.StageOrder || i + 1) * 2 })),
      ...services.map((s) => ({ kind: 'service', service: s, sortKey: s.AfterStageOrder * 2 + 1 })),
    ];
    rows.sort((a, b) => a.sortKey - b.sortKey);
    return rows;
  }, [stages, services]);

  const classificationLabel = (classification) => {
    if (!classification) return '';
    return classification.ClassificationName || '';
  };

  const selectedGroupLabel = React.useMemo(() => {
    const serializedSelected = serializeGroupValue(selectedGroupValue);
    const match = groupOptions.find(
      (group) => serializeGroupValue(group.value) === serializedSelected,
    );
    return match?.name || groupOptions[0]?.name || '';
  }, [groupOptions, selectedGroupValue]);

  const competitionOptions = competitions.map((competition, index) => ({
    value: index,
    label: competition.NameCompetition,
  }));

  const classificationOptions = classifications.map((classification, index) => ({
    value: index,
    label: classificationLabel(classification),
  }));

  const groupOptionList = groupOptions.map((group) => ({
    value: serializeGroupValue(group.value),
    label: group.name,
  }));

  const stageOptions = stages.map((stage, index) => ({
    value: index,
    label: stage.StageType,
    timingPhase: resolvePhase(stage),
  }));

  // Stage options interleaved with service info rows for the mobile sheet
  const combinedSheetStageOptions = useMemo(() => {
    const items = [];
    combinedStageRows.forEach((row) => {
      if (row.kind === 'stage') {
        items.push({
          value: row.index,
          label: row.stage.StageType,
          stageName: row.stage.StageDetails || row.stage.Name || '',
          timingPhase: resolvePhase(row.stage),
        });
      } else {
        const s = row.service;
        const duration = formatDuration(s.DurationMinutes);
        const detail = [s.StartTime, s.Location].filter(Boolean).join('  ·  ');
        items.push({ kind: 'info', duration, detail, name: s.Name });
      }
    });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedStageRows, stagePhases]);

  const renderActiveSheet = () => {
    if (!activeSheet) return null;

    switch (activeSheet) {
      case 'competition':
        return (
          <OptionSheet
            title="Wybierz zawody"
            options={competitionOptions}
            selectedValue={competitionIndex}
            onSelect={(value) => onCompetitionChange(Number(value))}
            onClose={() => setActiveSheet(null)}
          />
        );
      case 'classification':
        return (
          <OptionSheet
            title="Wybierz klasyfikację"
            options={classificationOptions}
            selectedValue={classificationIndex}
            onSelect={(value) => onClassificationChange(Number(value))}
            onClose={() => setActiveSheet(null)}
          />
        );
      case 'group':
        return (
          <OptionSheet
            title="Wybierz Klase"
            options={groupOptionList}
            selectedValue={serializeGroupValue(selectedGroupValue)}
            onSelect={(value) => onGroupChange(JSON.parse(value))}
            onClose={() => setActiveSheet(null)}
          />
        );
      case 'stage':
        return (
          <OptionSheet
            title="Wybierz odcinek"
            options={combinedSheetStageOptions}
            selectedValue={stageIndex}
            onSelect={(value) => onStageChange(Number(value))}
            onClose={() => setActiveSheet(null)}
            renderLabel={(option) =>
              option.kind === 'info' ? null : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: PHASE_DOT[option.timingPhase || 'NOT_STARTED']?.color,
                  }} />
                  {option.label}
                  {option.stageName && (
                    <span style={{ opacity: 0.6, fontSize: '0.85em', marginLeft: 2 }}>
                      {option.stageName}
                    </span>
                  )}
                </span>
              )
            }
          />
        );
      default:
        return null;
    }
  };

  if (isMobile) {
    return (
      <div className="mt-3">
        <div>
          <div className="content columns">
            <div className="mobile-filters-panel">
              <MobileDropdown
                colorClass={`mobile-dropdown--muted ${
                  activeSheet === 'competition' ? 'mobile-dropdown--secondary' : ''
                }`}
                label="Zawody"
                onClick={() => setActiveSheet('competition')}
              >
                {competitionOptions[competitionIndex]?.label || 'Wybierz zawody'}
              </MobileDropdown>

              <MobileDropdown
                colorClass={`mobile-dropdown--muted ${
                  activeSheet === 'classification' ? 'mobile-dropdown--secondary' : ''
                }`}
                label="Klasyfikacja"
                onClick={() => setActiveSheet('classification')}
              >
                {classificationLabel(activeClassification) || 'Wybierz klasyfikację'}
              </MobileDropdown>

              <MobileDropdown
                colorClass={`mobile-dropdown--muted ${
                  activeSheet === 'group' ? 'mobile-dropdown--secondary' : ''
                }`}
                label="Klasa"
                onClick={() => setActiveSheet('group')}
              >
                {selectedGroupLabel || 'Wybierz klasę'}
              </MobileDropdown>

              <MobileDropdown
                colorClass={`mobile-dropdown--muted ${
                  activeSheet === 'stage' ? 'mobile-dropdown--secondary' : ''
                }`}
                label="Odcinek"
                onClick={() => setActiveSheet('stage')}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%', overflow: 'hidden' }}>
                  <span style={{
                    display: 'inline-block', width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: PHASE_DOT[stageOptions[stageIndex]?.timingPhase || 'NOT_STARTED']?.color,
                    marginRight: 5,
                  }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {stageOptions[stageIndex]?.label || 'Wybierz odcinek'}
                    {activeStage?.StageDetails ? ` · ${activeStage.StageDetails}` : ''}
                  </span>
                </span>
              </MobileDropdown>
              <div
                className="mobile-stage-toggle mobile-filters-panel__toggle"
                role="group"
                aria-label="Widok wyników"
              >
                <button
                  type="button"
                  className={`mobile-stage-toggle__button ${
                    viewMode === 'odcinkowa' ? 'is-active' : ''
                  }`}
                  onClick={() => onViewModeChange('odcinkowa')}
                >
                  ODCINKOWA
                </button>
                <button
                  type="button"
                  className={`mobile-stage-toggle__button ${
                    viewMode === 'po-odcinku' ? 'is-active' : ''
                  }`}
                  onClick={() => onViewModeChange('po-odcinku')}
                >
                  PO ODCINKU
                </button>
              </div>
              {renderActiveSheet()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-header">
      <div className="header">
        <div className="competition-section">
          <div className="competition-logo">
            {logo && <img src={logo} alt="logo" className="logo-image" />}
          </div>
          <div className="competition-buttons">
            {competitions.map((competition, index) => (
              <div
                key={competition.IDCompetition}
                className={`competition-button ${
                  index === competitionIndex ? 'selected' : ''
                }`}
                onClick={() => onCompetitionChange(index)}
              >
                <div className="competition-name">{competition.NameCompetition}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="classification-buttons">
          {classifications.map((classification, index) => (
            <div
              key={classification.ClassificationID}
              className={`classification-button ${
                index === classificationIndex ? 'selected' : ''
              }`}
              onClick={() => onClassificationChange(index)}
            >
              <div className="classification-name">{classification.ClassificationName}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="content">
        <div className="location-info">
          <div className="location-name">
            {activeStage?.StageDetails || 'Wybierz odcinek'}
          </div>
        </div>

        <div className="column">
          <div className="stage-timeline">
            {combinedStageRows.map((row, i) => {
              if (row.kind === 'service') {
                const s = row.service;
                const duration = formatDuration(s.DurationMinutes);
                return (
                  <div
                    key={`svc-${s.ID || i}`}
                    className="stn-node stn-node--service"
                  >
                    <div className="stn-dot"><WrenchSVG /></div>
                    <div className="stn-chip">{duration || s.Name}</div>
                  </div>
                );
              }

              const { stage, index } = row;
              const status = getStageStatus(resolvePhase(stage));
              const isSelected = index === stageIndex;
              return (
                <div
                  key={stage.StageID}
                  className={`stn-node stn-node--${status}${isSelected ? ' stn-node--selected' : ''}`}
                  onClick={() => onStageChange(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && onStageChange(index)}
                  aria-pressed={isSelected}
                  aria-label={`${stage.StageType}${isSelected ? ' — wybrany' : ''}`}
                >
                  <div className="stn-dot">
                    {status === 'done' && <CheckSVG />}
                  </div>
                  <div className="stn-chip">{stage.StageType}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsFilters;
